import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { useMemo } from 'react'
import { Address } from 'viem'
import { useReadContracts } from 'wagmi'

interface CollateralItem {
  vaultAddress: string
  depositAmountUSD: number
}

/**
 * Queries LTVFull on a borrow vault for each collateral and returns the
 * borrow-LTV-weighted total collateral value in USD.
 *
 * Returns `undefined` while the query is in flight.
 */
export function useLtvWeightedCollateral({
  borrowVaultAddress,
  collateral,
  chainId,
}: {
  borrowVaultAddress: Address | undefined
  collateral: CollateralItem[]
  chainId: number
}) {
  const ltvContracts = useMemo(() => {
    if (!borrowVaultAddress || collateral.length === 0) return []
    return collateral.map((col) => ({
      address: borrowVaultAddress,
      abi: eVaultImplementationAbi,
      functionName: 'LTVFull' as const,
      args: [col.vaultAddress as Address],
      chainId,
    }))
  }, [borrowVaultAddress, collateral, chainId])

  const { data: ltvResults } = useReadContracts({
    contracts: ltvContracts,
    query: {
      enabled: ltvContracts.length > 0,
      staleTime: 30_000,
    },
  })

  return useMemo(() => {
    if (!ltvResults) return undefined
    return collateral.reduce((sum, col, idx) => {
      const result = ltvResults[idx]
      if (result?.status !== 'success') return sum
      // LTVFull returns: [borrowLTV, liquidationLTV, initialLiquidationLTV, targetTimestamp, rampDuration]
      // borrowLTV is uint16 in basis points (10000 = 100%)
      const [borrowLTV] = result.result as readonly [number, number, number, number, number]
      return sum + col.depositAmountUSD * ((borrowLTV ?? 0) / 10000)
    }, 0)
  }, [ltvResults, collateral])
}
